import {
  type Configuration,
  type IPublicClientApplication,
  PublicClientApplication,
} from "@azure/msal-browser";
import { getCwhMsalController } from "@ingka-group-digital/cwh-msal-controller";
import { msalConfiguration } from "@coworker/apprestructured/src/assets/auth/msalConfig";

// Cast required because cwep-hej-core bundles a different @azure/msal-browser
// version (v4) than Fixa (v5). HejMsalConfig is structurally compatible
// with v5 Configuration — it only adds the optional protectedResourceScopeMap field.
const msalConfig = msalConfiguration as unknown as Configuration;

// ── Shell MFE override ────────────────────────────────────────────────────────
// When cmp-fixa runs inside the Flexa shell, mfe.tsx calls setMsalInstance()
// with a shell-backed adapter (createShellMsalInstance) so that all token
// acquisitions are brokered by the shell instead of triggering a redirect.
let _shellMsalOverride: IPublicClientApplication | null = null;

export const setMsalInstance = (instance: IPublicClientApplication): void => {
  _shellMsalOverride = instance;
};

/**
 * Returns true when cmp-fixa is running as a Flexa shell MFE.
 * Use this to skip auth flows that are only valid in standalone mode
 */
export const isMfeMode = (): boolean => _shellMsalOverride !== null;

/**
 * Returns the MSAL instance to use for token acquisition.
 *
 * - In standalone mode: resolves the real PublicClientApplication singleton.
 * - In MFE mode (shell): immediately resolves the shell-backed adapter set by
 *   mfe.tsx via setMsalInstance(), skipping Azure AD initialisation entirely.
 */
export const getMsalInstanceAsync = (): Promise<IPublicClientApplication> => {
  if (_shellMsalOverride) return Promise.resolve(_shellMsalOverride);
  return initMsalSingleton();
};

// ── Standalone singleton ──────────────────────────────────────────────────────
// Lazily initialized — only created the first time getMsalInstanceAsync() is
// called without a shell override.  This prevents PublicClientApplication from
// running (and potentially triggering a redirect) when the module is imported
// inside the Flexa shell sandbox.
let _msalSingleton: Promise<IPublicClientApplication> | null = null;

const initMsalSingleton = (): Promise<IPublicClientApplication> => {
  if (!_msalSingleton) {
    _msalSingleton = (async () => {
      const msalInstance = new PublicClientApplication(
        msalConfig,
        getCwhMsalController(msalConfig)
      );

      await msalInstance.initialize();
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0] ?? null);
      }

      return msalInstance;
    })();
  }
  return _msalSingleton;
};
